/**
 * Created by liorb on 5/4/2017.
 */
import dayjs from "dayjs";
import * as _ from "lodash";

import Sentry from "@similarweb/sentry-logger";
import { colorsPalettes } from "@similarweb/styles";

import { swSettings } from "app/common/services/swSettings";
import { generateLineChartData } from "pro-features/components/Chart/src/components/PeriodOverPeriodChart/PoPChartDataGenerator";
import type { ISeries } from "pro-features/components/Chart/src/components/PeriodOverPeriodChart/PoPChartTypes";
import { GraphTemplateV2 } from "app/components/widget/dashboard/GraphTemplate-v2";
import type { EngagementPoPGraphResponse } from "app/pages/app-intelligence/app-analysis/pages/Engagement/queries/types";
import { getStoreEngagementVerticals } from "app/pages/app-intelligence/app-analysis/pages/Engagement/Store/StoreEngagementVerticals";
import { EDevice } from "app/pages/app-intelligence/common-components/device-picker/types";
import DurationService, { apiFormat } from "app/services/DurationService";

import { ChartMarkerService } from "../../../services/ChartMarkerService";

import { getDisplayName, getIntervals, getPOPLegend, transformToPopData } from "../utils/utils.apps";
import { GraphWidget } from "./GraphWidget";
import { APPLE_STORE, EStores } from "app/pages/app-intelligence/constants";

import { WidgetState, WidgetMessages } from "./Widget";

export class AppEngagementMetricsDashboardGraphWidget extends GraphWidget {
    private metricDataKeyMap = {
        AppEngagementDownloads: "Downloads",
        AppEngagementUniqueInstalls: "UniqueInstalls",
        AppEngagementCurrentInstalls: "CurrentInstalls",
        AppEngagementDailyActiveUsers: "DailyActiveUsers",
        AppEngagementDailyActiveUsersIos: "DailyActiveUsers",
        AppEngagementMonthlyActiveUsersIos: "MonthlyActiveUsers",
        AppEngagementWeeklyActiveUsers: "WeeklyActiveUsers",
        AppEngagementMonthlyActiveUsers: "MonthlyActiveUsers",
        AppEngagementDailyShareUsers: "DailyShareUsers",
        AppEngagementUsageTime: "UsageTime",
        AppEngagementSessionsPerUser: "SessionsPerUser",
        AppEngagementUsagePenetration: "UsagePenetration",
        AppUsersEngagementAvgSessionsPerUser: "AvgSessionsPerUser",
        AppUsersEngagementAvgUsageTimePerSession: "AvgUsageTimePerSession",
        AppUsersEngagementAvgTotalUsageTimePerUser: "AvgTotalUsageTimePerUser",
        AppEngagementDailyStickiness: "DailyStickiness",
    };
    // This values used to generate POP legend
    public periodOverPeriodLegend = true;
    public periodOverPeriodLegendItems;
    public customLegendTemplate = null;

    public runWidget() {
        const isPoP = !!this.apiParams.compareFrom;

        if (isPoP) {
            this.customLegendTemplate = `/partials/directives/comparedDurationLegend.html`;
        }
        super.runWidget();
    }

    static getWidgetMetadataType() {
        return "AppEngagementMetricsDashboardGraph";
    }

    static getWidgetResourceType() {
        return "Data";
    }

    static getWidgetDashboardType() {
        return "Graph";
    }
    get templateComponent() {
        return GraphTemplateV2;
    }
    get templateUrl() {
        return `/app/components/widget/widget-templates/graph.html`;
    }

    protected async getChartSeries(unorderedData: any, compareItemsKeys: string[]): Promise<any[]> {
        let noDataKeys = 0; // count how many apps have no data
        let appData;
        const colors = this._highchartsConfig.newColorPaletteColors();
        const formattedData = {};
        _.each(Object.keys(unorderedData), (app) => {
            formattedData[app] = {};
            const crrKey = Object.keys(unorderedData[app])[0];
            appData = unorderedData[app][crrKey];
            if (!appData) {
                formattedData[app] = null;
                noDataKeys++;
            } else {
                formattedData[app][crrKey] = appData;
            }
        });
        // if all selected apps has no data we need to show the 'no data' message
        if (noDataKeys === Object.keys(unorderedData).length) {
            return [];
        } else {
            const series = await super.getChartSeries(formattedData, Object.keys(unorderedData));
            // use colors of legend if available
            return _.isEmpty(this.legendItems)
                ? series
                : series.map((serie) => {
                      const legendIndex = this.legendItems.findIndex((item) => item.name === serie.name);
                      return {
                          ...serie,
                          color: colors[legendIndex],
                          marker: {
                              symbol: ChartMarkerService.createMarkerStyle(colors[legendIndex]).background,
                          },
                      };
                  });
        }
    }

    getData() {
        const metric = this.getWidgetModel().metric;
        // This hack allows us to change metric depending on device OS
        if (this._params.store === "Apple" && metric === "AppEngagementWeeklyActiveUsers") {
            this._params.metric = "AppEngagementUserActivity";
        }

        return super.getData();
    }

    public getProUrl() {
        const store = this.apiParams.store === APPLE_STORE ? EStores.Apple : EStores.Google;
        const metric = this.getWidgetModel().metric;
        const { country, keys } = this.apiParams;
        const duration = this.getDuration();

        const url = getStoreEngagementVerticals().meta.some((appMeta) => appMeta.metric === metric)
            ? "digitalsuite_app_storeengagement"
            : "digitalsuite_app_usersengagement";

        return this._swNavigator.getStateUrl(url, {
            tab: this.metricDataKeyMap[metric],
            timeGranularity: this.apiParams.timeGranularity,
            country,
            duration,
            appId: `${store}_${keys}`,
        });
    }

    protected getDuration(): string {
        const { isWindow, from, to } = this.apiParams;
        const componentId = this._metricConfig.component;
        const component = swSettings.components[componentId];
        const snapshotEndDate = component?.endDate;
        const windowEndDate = component?.windowEndDate;
        const toFormatted = dayjs.utc(to, apiFormat);
        const fromFormatted = dayjs.utc(from, apiFormat);
        const isLastXMonthsOrDays = toFormatted.isSame(snapshotEndDate) || toFormatted.isSame(windowEndDate);
        const allowedDiff = ["28d", "1m", "3m", "6m", "12m", "25m"];
        const durationAsLastXMonthsOrDays = DurationService.getDiffSymbol(from, to, isWindow ? "days" : "months");
        return isLastXMonthsOrDays && allowedDiff.some((allowedDiff) => allowedDiff === durationAsLastXMonthsOrDays)
            ? durationAsLastXMonthsOrDays
            : DurationService.getDiffCustomRangeParam(fromFormatted, toFormatted);
    }

    public async callbackOnGetData(response: any, comparedItemKeys?: any[]): Promise<void> {
        const isPOP = !!this._params.compareFrom;

        if (isPOP) {
            // Behavior completely different in case of POP
            return this.initPopChart(response as EngagementPoPGraphResponse);
        }

        const metric = this.getWidgetModel().metric;
        const metricResponse = { Data: {} };
        const keys = Object.keys(response.Data);

        keys.forEach((appId) => {
            let appData;

            try {
                appData =
                    response.Data[appId].Overtime[this.apiParams.timeGranularity][EDevice.ALL_DEVICES][
                        this.metricDataKeyMap[metric]
                    ];
            } catch (error) {
                Sentry.error("AppEngagementMetricsDashboardGraphWidget - Could not parse received data", error);
            }
            metricResponse.Data[appId] = { [this.metricDataKeyMap[metric]]: appData };
        });
        await super.callbackOnGetData(metricResponse, comparedItemKeys);
    }

    protected validateData(data: { ComparedInterval?: unknown; Interval?: unknown; Data?: unknown }): boolean {
        const isPOP = !!this.apiParams.compareFrom;

        if (isPOP && data?.Interval) {
            return true;
        }

        if (data?.Data) {
            return true;
        }

        return false;
    }

    async onNewDataReceived(response) {
        let dataValid;

        const isPOP = !!this.apiParams.compareFrom;

        const newResponse = isPOP
            ? {
                  Interval: { ...response?.Interval },
                  ComparedInterval: { ...response?.ComparedInterval },
              }
            : response;

        try {
            dataValid = this.validateData(newResponse);
        } catch (error) {
            Sentry.error(`Data Validation failed: `, error);
        }

        if (!dataValid) {
            this.widgetState = WidgetState.ERROR;
            this.emit(WidgetMessages.WIDGET_GET_DATA_SUCCESS, response);
            this.handleDataError(404);
            return;
        }

        this.widgetState = WidgetState.LOADED;

        this.originalData = newResponse;
        await this.callbackOnGetData((this._widgetConfig.processResponse || _.identity)(newResponse));

        this.emit(WidgetMessages.WIDGET_GET_DATA_SUCCESS, newResponse);
    }

    public getPopLineChart(response: EngagementPoPGraphResponse) {
        const { keys, granularity, device } = this._params;
        const metric = this.getWidgetModel().metric;

        const appId = keys.split(",")[0];
        //BE not sends device prop if only available one device (premium+)
        const fixedDevice = device === EDevice.COMBINED ? EDevice.ALL_DEVICES : !device ? EDevice.ALL_DEVICES : device;

        const fixedMauMetric = this.metricDataKeyMap[metric] || metric;

        const { comparedInterval, interval } = getIntervals(response, appId, granularity, fixedDevice, fixedMauMetric);

        const comparePoPData = transformToPopData(interval, comparedInterval) as ISeries[];
        const lineChartData = generateLineChartData([fixedDevice], comparePoPData);

        lineChartData.forEach((data) => {
            const texts = this.durationObject?.forWidget;
            if (data.color === colorsPalettes.orange[400]) {
                data["name"] = texts[1];
                return;
            }
            data["name"] = texts[0];
        });

        return lineChartData;
    }

    public initPopChart(popResponse: EngagementPoPGraphResponse) {
        const { device, store } = this._params;

        const formattedData = this.getPopLineChart(popResponse);
        const name = getDisplayName(device, store);

        const options = this.getChartOptions(formattedData);

        this.periodOverPeriodLegendItems = getPOPLegend(this.durationObject?.forWidget).map((legend, index) => {
            const color = formattedData[index].color;
            const items = [{ name, color, stack: index }];

            return { ...legend, items, visible: true };
        });

        this.chartConfig = {
            ...this.getHighChartsConfig(options, formattedData, []),
        };

        this.chartConfig.series = formattedData;

        this.originalData = popResponse;
        this.notifyWidgetUpdated();
    }
}

AppEngagementMetricsDashboardGraphWidget.register();
